import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  IconButton,
  Box,
  useTheme,
} from '@mui/material';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import CloseIcon from '@mui/icons-material/Close';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useThemeContext } from '../../Context/ThemeContext';


const ExitConfirmationModal = ({ open, onClose, onConfirm, onSave }) => {
  const { darkMode } = useThemeContext();
  const theme = useTheme();
  const navigate = useNavigate();
  const borderBase = 'rgba(79, 172, 254, 0.1)';

  const handleDontSave = () => {
    onConfirm();
    navigate('/userdashboard');
  };

  const handleSave = () => {
    onSave();
    navigate('/userdashboard');
  };


  return (
    <Dialog
      open={open}
      onClose={() => onClose('cancel')}
      PaperProps={{
        elevation: 0,
        sx: {
          borderRadius: 3,
          width: 480,
          overflow: 'hidden',
          backgroundColor: darkMode ? '#1e1f22' : '#ffffff',
          border: `1px solid ${borderBase}`,
          boxShadow: darkMode
            ? '0 24px 64px rgba(0,0,0,0.5), inset 0 1px 2px rgba(255,255,255,0.04)'
            : '0 24px 64px rgba(0,0,0,0.15), inset 0 1px 2px rgba(0,0,0,0.06)',
          backdropFilter: 'blur(4px)',
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: darkMode
            ? 'rgba(10, 14, 20, 0.55)'
            : 'rgba(10, 18, 30, 0.35)',
          backdropFilter: 'blur(4px)',

          '& .MuiDialog-container': {
            alignItems: 'center',
          },
        },
      }}
      transitionDuration={{ appear: 250, enter: 250, exit: 200 }}
    >
      {/* Close Button */}
      <Box sx={{ position: 'absolute', top: 10, right: 10, zIndex: 1 }}>
        <IconButton
          onClick={() => onClose('cancel')}
          size="small"
          sx={{
            color: theme.palette.text.primary,
            bgcolor: darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)',
            border: `1px solid ${borderBase}`,
            '&:hover': {
              bgcolor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
              boxShadow: '0px 4px 10px 0px #0000000D',
              // borderColor: hoverBorder,
              // boxShadow: focusRing,
            },
            transition: 'all .2s ease',
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Content */}
      <DialogContent
        sx={{
          py: 5,
          px: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1.5,
          textAlign: 'center',
        }}
      >
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            display: 'grid',
            placeItems: 'center',
            mb: 1,
            background: darkMode ? '#2c2c2e' : '#f7fbff',
            border: `1px solid ${borderBase}`,
            boxShadow: darkMode
              ? 'inset 0 1px 2px rgba(255,255,255,0.04)'
              : 'inset 0 1px 2px rgba(0,0,0,0.06)',
          }}
        >
          <WarningAmberOutlinedIcon
            sx={{ fontSize: '2rem', color: '#FFA726' }}
          />
        </Box>

        <Typography
          sx={{
            fontSize: '0.8rem',
            fontWeight: 600,
            letterSpacing: 0.2,
            color: theme.palette.text.primary,
            mb: 2
          }}
        >
          Exit confirmation
        </Typography>

        <Typography
          sx={{
            fontSize: '0.95rem',
            fontWeight: 500,
            color: theme.palette.text.secondary,
            mt: 0.5,
          }}
        >
          Are you sure you want to exit without saving?
        </Typography>
         <Typography
          variant="body2"
          sx={{
            fontSize: '0.875rem',
            fontWeight: 400,
            color: theme.palette.text.secondary,
          }}
        >
          Changes you made will be lost unless saved.
        </Typography>
      </DialogContent>

      {/* Buttons */}
      <DialogActions
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1.5,
          px: 4,
          pb: 4,
        }}
      >
        {/* Don't Save Button */}
        <Button
          variant="outlined"
          onClick={handleDontSave}  // This will trigger the exit action without saving and navigate
          sx={{
            borderRadius: '8px',
            textTransform: 'none',
        
            fontSize: '0.95rem',
            fontWeight: 500,
            px: 4,
            borderColor: 'rgba(79, 172, 254, 0.59)',
            color: '#4facfe',
            '&:hover': {
              borderColor: '#4facfe',
              background: 'rgba(79, 172, 254, 0.05)',
            },
          }}
        >
          Don&apos;t Save
        </Button>

       

        {/* Save Button - Primary matches NewWorkflowModal blue accent */}
        <Button
          variant="contained"
          onClick={handleSave}
          sx={{
            minWidth: 110,
            height: 40,
            borderRadius: 2,
            px: 4,
            fontWeight: 600,
            textTransform: 'none',
            boxShadow: 'none',
            fontSize: '0.95rem',
            backgroundImage: 'linear-gradient(90deg, #3b9cfe, #00c4ef)',
            color: '#fff',
            '&:hover': {
              backgroundImage: 'linear-gradient(90deg, #3b9cfe, #00c4ef)',
              boxShadow: '0 8px 20px rgba(79, 172, 254, 0.25)',
              transform: 'translateY(-1px)',
            },
            '&:active': {
              transform: 'translateY(0)',
              boxShadow: 'none',
            },
            transition: 'all .18s ease',
          }}
        >
          Save
        </Button>

         {/* Cancel Button */}
        {/* <Button
          variant="text"
          onClick={() => onClose('cancel')}
          sx={{
            minWidth: 120,
            borderRadius: '6px',
            fontWeight: 500,
            textTransform: 'none',
            fontSize: '0.875rem',
            color: theme.palette.text.secondary,
          }}
        >
          Cancel
        </Button> */}
      </DialogActions>
    </Dialog>
  );
};

ExitConfirmationModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,  // New prop for save action
};

export default ExitConfirmationModal;
